
export interface CustomFieldValue {
  text?: string;
  number?: number;
  boolean?: boolean;
  json?: object;
  /** MULTI_SELECT: the chosen options, by id. */
  option_ids?: string[];
}

/** How to retire a multi-select option that records already hold. */
export enum CustomFieldOptionRemovalMode {
  /** Keep the value readable and filterable, but stop it being chosen. Nothing is lost. */
  ARCHIVE = 'archive',
  /** Remove it from every record and delete it, as deleting a label does. Cannot be undone. */
  DELETE = 'delete',
}

/**
 * One selectable option, as both dropdown types expose it.
 *
 * MULTI_SELECT returns its real rows; SELECT has no option rows, so the server synthesises each as
 * `{id: label}` — which is right, because a SELECT value IS its label. That is also why renaming a
 * SELECT option orphans its stored values and renaming a MULTI_SELECT one does not.
 */
export interface ICustomFieldSelectOption {
  id: string;
  label: string;
  position: number;
  archived_at: string | null;
}

export enum CustomFieldType {
  TEXT = 'text',
  SELECT = 'select',
  BOOLEAN = 'boolean',
  /**
   * Pick any number of options. Its options are rows with stable ids, not the bare strings SELECT
   * keeps — so renaming one leaves stored values intact and the list column filter matches an id.
   */
  MULTI_SELECT = 'multi_select'
}

export enum CustomFieldEntityType {
  CONTACT = 'contact',
  COMPANY = 'company',
  CANDIDATE = 'candidate',
  JOB = 'job',
  DEAL = 'deal',
  PLACEMENT = 'placement'
}

export type UpdateCustomFieldValueDto = {
  fields: {
    config_id: string;
    value: CustomFieldValue;
  }[];
}

export type UpdateCustomFieldConfigDto = {
  configs: {
    id?: string;
    field_type: CustomFieldType;
    label: string;
    /** SELECT only. */
    options?: string[];
    /**
     * MULTI_SELECT only: the full desired option list in display order. An entry with an id renames
     * that option in place; one without is created. Options omitted here are retired per
     * `removed_options`.
     */
    select_options?: { id?: string; label: string }[];
    /** How to retire each option dropped from `select_options`. Anything unlisted is archived. */
    removed_options?: { id: string; mode: CustomFieldOptionRemovalMode }[];
    is_required?: boolean;
  }[];
}

export interface ICustomField {
  id: string;
  config: ICustomFieldConfig;
  value: CustomFieldValue;
}

export interface ICustomFieldConfig {
  id: string;
  field_type: CustomFieldType;
  label: string;
  /** SELECT only. Read `select_options` instead — it covers both dropdown types. */
  options?: string[];
  select_options?: ICustomFieldSelectOption[];
  is_required?: boolean;
  entity_type: CustomFieldEntityType;
  created_at: string;
  updated_at: string;
}